import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building, User, Package, LogOut, Wallet, X, Trash2, Clock, Truck, Store, Check, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  new: { label: 'Nouvelle', color: '#F78A7D', bg: 'rgba(247,138,125,0.1)' },
  preparing: { label: 'En preparation', color: '#EAB308', bg: 'rgba(234,179,8,0.1)' },
  ready: { label: 'Prete', color: '#6DAE65', bg: 'rgba(109,174,101,0.1)' },
  delivered: { label: 'Livree', color: '#3B82F6', bg: 'rgba(59,130,246,0.1)' },
  cancelled: { label: 'Annulee', color: '#EF4444', bg: 'rgba(239,68,68,0.1)' },
};

export default function Compte() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [wallet, setWallet] = useState(0);
  const [cancellingId, setCancellingId] = useState(null);
  const [cancelItems, setCancelItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('cg-customer'));
      if (saved) {
        setUser(saved);
        loadData(saved.id);
      } else {
        navigate('/');
      }
    } catch { navigate('/'); }
  }, [navigate]);

  const loadData = async (id) => {
    const [ordRes, walRes] = await Promise.all([
      axios.get(`${API}/api/customers/${id}/orders`).catch(() => ({ data: [] })),
      axios.get(`${API}/api/customers/${id}/wallet`).catch(() => ({ data: { balance: 0 } })),
    ]);
    setOrders(ordRes.data);
    setWallet(walRes.data.balance || 0);
  };

  const handleLogout = () => {
    localStorage.removeItem('cg-customer');
    navigate('/');
  };

  const handleCancelFull = async (orderId) => {
    if (!window.confirm('Annuler cette commande complete ? Le montant sera credite a votre portefeuille.')) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/orders/${orderId}/cancel`, { customer_id: user.id, remove_items: [] });
      alert(`Commande annulee. ${data.credit?.toFixed(2)} $ credites a votre portefeuille.`);
      loadData(user.id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
    setLoading(false);
  };

  const handleCancelItems = async (orderId) => {
    if (cancelItems.length === 0) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/orders/${orderId}/cancel`, { customer_id: user.id, remove_items: cancelItems });
      alert(`${cancelItems.length} item(s) retire(s). ${data.credit?.toFixed(2)} $ credites a votre portefeuille.`);
      setCancellingId(null);
      setCancelItems([]);
      loadData(user.id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la modification');
    }
    setLoading(false);
  };

  const toggleCancelItem = (name, format) => {
    const key = `${name}_${format}`;
    setCancelItems(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  if (!user) return null;

  const canModify = (order) => !['delivered', 'cancelled'].includes(order.status);

  return (
    <div data-testid="compte-page" className="min-h-screen py-20 px-4">
      <div className="max-w-3xl mx-auto">

        {/* Profile + Wallet */}
        <div className="p-6 rounded-2xl mb-6" style={{ backgroundColor: 'var(--color-muted)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold text-white"
              style={{ backgroundColor: 'var(--color-primary)' }}>
              {user.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-[200px]">
              <h2 className="text-xl font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{user.name}</h2>
              <p className="text-sm" style={{ color: 'var(--color-muted-fg)' }}>{user.email}</p>
              {user.type === 'corporate' && user.company && (
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full mt-1"
                  style={{ backgroundColor: 'rgba(109,174,101,0.15)', color: 'var(--color-secondary)' }}>
                  <Building size={10} /> {user.company}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl" style={{ backgroundColor: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.2)' }}>
                <Wallet size={18} style={{ color: '#EAB308' }} />
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: 'rgba(234,179,8,0.6)' }}>Portefeuille</p>
                  <p className="text-lg font-bold" style={{ color: '#EAB308' }}>{wallet.toFixed(2)} $</p>
                </div>
              </div>
              <button onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all hover:scale-105"
                style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.2)' }}>
                <LogOut size={14} /> Deconnexion
              </button>
            </div>
          </div>
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
          <Link to="/commande"
            className="flex items-center gap-3 p-4 rounded-2xl transition-all duration-300 hover:-translate-y-1"
            style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(247,138,125,0.1)' }}>
              <Package size={20} style={{ color: 'var(--color-primary)' }} />
            </div>
            <div>
              <p className="font-semibold text-sm">Commander</p>
              <p className="text-xs" style={{ color: 'var(--color-muted-fg)' }}>Passer une commande</p>
            </div>
          </Link>
          <div className="flex items-center gap-3 p-4 rounded-2xl"
            style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(59,130,246,0.1)' }}>
              <Clock size={20} style={{ color: '#3B82F6' }} />
            </div>
            <div>
              <p className="font-semibold text-sm">{orders.length} commande{orders.length !== 1 ? 's' : ''}</p>
              <p className="text-xs" style={{ color: 'var(--color-muted-fg)' }}>Historique ci-dessous</p>
            </div>
          </div>
        </div>

        {/* Order History */}
        <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Historique des commandes</h3>

        {orders.length === 0 ? (
          <div className="text-center py-12 rounded-2xl" style={{ backgroundColor: 'var(--color-muted)', border: '1px solid var(--color-border)' }}>
            <Package size={40} className="mx-auto mb-3" style={{ color: 'var(--color-muted-fg)' }} />
            <p className="text-sm" style={{ color: 'var(--color-muted-fg)' }}>Aucune commande pour le moment</p>
          </div>
        ) : orders.map(order => {
          const st = STATUS_MAP[order.status] || STATUS_MAP.new;
          const isExpanded = cancellingId === order.id;
          return (
            <div key={order.id} className="rounded-2xl overflow-hidden mb-4" style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
              {/* Header */}
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold">#{order.id?.slice(0, 8)}</span>
                  <span className="text-xs" style={{ color: 'var(--color-muted-fg)' }}>{order.created_at ? new Date(order.created_at).toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: st.bg, color: st.color }}>{st.label}</span>
                </div>
                <span className="text-lg font-bold" style={{ color: 'var(--color-primary)' }}>{order.total?.toFixed(2)} $</span>
              </div>

              {/* Items */}
              <div className="px-4 pb-3 space-y-1">
                {(order.items || []).map((item, i) => {
                  const itemKey = `${item.name}_${item.format}`;
                  const isMarked = cancelItems.includes(itemKey) && isExpanded;
                  return (
                    <div key={i} className={`flex items-center justify-between text-sm py-1.5 px-2 rounded-lg transition-all ${isMarked ? 'line-through opacity-50' : ''}`}
                      style={{ backgroundColor: isMarked ? 'rgba(239,68,68,0.05)' : 'transparent' }}>
                      <span style={{ color: 'var(--color-fg)' }}>{item.name} <span className="text-xs" style={{ color: 'var(--color-muted-fg)' }}>({item.format}) x{item.qty}</span></span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{(item.price * item.qty).toFixed(2)} $</span>
                        {isExpanded && (
                          <button onClick={() => toggleCancelItem(item.name, item.format)}
                            className="p-1 rounded-md transition-all hover:bg-red-400/10"
                            style={{ color: isMarked ? '#6DAE65' : '#EF4444' }}>
                            {isMarked ? <Check size={14} /> : <Trash2 size={14} />}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Delivery info */}
              <div className="px-4 pb-3 flex gap-3 text-xs" style={{ color: 'var(--color-muted-fg)' }}>
                <span className="flex items-center gap-1">{order.delivery === 'delivery' ? <><Truck size={12} /> Livraison</> : <><Store size={12} /> Cueillette</>}</span>
                {order.form?.date && <span className="flex items-center gap-1"><Clock size={12} /> {new Date(order.form.date + 'T12:00:00').toLocaleDateString('fr-CA', { weekday: 'short', day: 'numeric', month: 'short' })}</span>}
                {order.delivery_zone && <span>Zone: {order.delivery_zone.name}</span>}
              </div>

              {/* Action buttons */}
              {canModify(order) && (
                <div className="px-4 pb-4 flex gap-2">
                  {!isExpanded ? (
                    <>
                      <button onClick={() => handleCancelFull(order.id)} disabled={loading}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all hover:scale-105 disabled:opacity-50"
                        style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.15)' }}>
                        <X size={12} /> Annuler la commande
                      </button>
                      <button onClick={() => { setCancellingId(order.id); setCancelItems([]); }}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all hover:scale-105"
                        style={{ backgroundColor: 'rgba(234,179,8,0.1)', color: '#EAB308', border: '1px solid rgba(234,179,8,0.15)' }}>
                        <Trash2 size={12} /> Retirer des items
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => handleCancelItems(order.id)} disabled={loading || cancelItems.length === 0}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all hover:scale-105 text-white disabled:opacity-50"
                        style={{ backgroundColor: '#EF4444' }}>
                        <Check size={12} /> Confirmer ({cancelItems.length} item{cancelItems.length !== 1 ? 's' : ''})
                      </button>
                      <button onClick={() => { setCancellingId(null); setCancelItems([]); }}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium"
                        style={{ color: 'var(--color-muted-fg)' }}>
                        Annuler
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
